"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";

const NAMESPACE = "new-sls-selective-laser-sintering";

const ROWS = Array.from({ length: 8 }, (_, i) => i + 1);

const COLS = ["Material", "Color", "TensileStrength", "TensileModulus", "Elongation"] as const;

type UnitSystem = "US" | "Metric";

function getCellValue(
	t: ReturnType<typeof useTranslations>,
	rowIndex: number,
	col: (typeof COLS)[number],
	unit: UnitSystem,
): string {
	const prefix = `materialPropertyRow${rowIndex}`;
	if (col === "TensileStrength" || col === "TensileModulus") {
		return t(`${prefix}${col}${unit}` as any);
	}
	return t(`${prefix}${col}` as any);
}

export default function SlsMaterialPropertyComparison() {
	const t = useTranslations(NAMESPACE);
	const [unit, setUnit] = useState<UnitSystem>("US");

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<h2 className="mb-6 text-2xl font-bold leading-tight text-black sm:mb-8 sm:text-[30px] md:mb-10 md:text-4xl lg:mb-12">
					{t("materialPropertyTitle")}
				</h2>
				<div className="mb-6 flex items-center justify-end border-b border-gray-300 sm:mb-8 md:mb-10 lg:mb-12">
					<button
						onClick={() => setUnit("US")}
						className={`relative px-4 pb-2 text-sm font-semibold transition-colors ${
							unit === "US"
								? "text-black after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:bg-[#64a525]"
								: "text-gray-400 hover:text-gray-600"
						}`}
					>
						{t("materialPropertyUnitUS")}
					</button>
					<button
						onClick={() => setUnit("Metric")}
						className={`relative px-4 pb-2 text-sm font-semibold transition-colors ${
							unit === "Metric"
								? "text-black after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:bg-[#64a525]"
								: "text-gray-400 hover:text-gray-600"
						}`}
					>
						{t("materialPropertyUnitMetric")}
					</button>
				</div>

				{/* Desktop table */}
				<div className="hidden overflow-x-auto md:block">
					<table className="w-full border-collapse text-sm text-black">
						<thead>
							<tr>
								{COLS.map((col) => (
									<th
										key={col}
										className="border-b-2 border-[#64a525] bg-[#64a525] px-4 py-4 text-left text-sm font-bold text-white first:pl-6 last:pr-6 lg:text-base"
									>
										{t(`materialPropertyCol${col}`)}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{ROWS.map((n) => (
								<tr key={n} className="bg-[#f6ffde]">
									{COLS.map((col) => (
										<td
											key={col}
											className="border-b border-[#d7dacc] px-4 py-5 align-top first:pl-6 last:pr-6"
										>
											{getCellValue(t, n, col, unit)}
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>

				{/* Mobile cards */}
				<div className="flex flex-col gap-4 md:hidden">
					{ROWS.map((n) => (
						<div key={n} className="overflow-hidden rounded-xl border border-[#d7dacc] bg-white">
							<div className="bg-[#64a525] px-4 py-3">
								<h4 className="text-sm font-bold text-white sm:text-base">
									{t(`materialPropertyRow${n}Material` as any)}
								</h4>
							</div>
							<div className="grid grid-cols-2 gap-px bg-gray-100">
								{COLS.slice(1).map((col) => (
									<div key={col} className="bg-white px-4 py-3">
										<p className="mb-1 text-xs font-semibold text-gray-500">
											{t(`materialPropertyCol${col}`)}
										</p>
										<p className="text-sm leading-relaxed text-black">{getCellValue(t, n, col, unit)}</p>
									</div>
								))}
							</div>
						</div>
					))}
				</div>

				<div className="mt-8 space-y-3">
					<p className="text-xs text-black sm:text-sm">{t("materialPropertyNote")}</p>
				</div>
			</div>
		</section>
	);
}
